import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { photoUrl } from "@/lib/api";

const fetchAsDataURL = async (ref) => {
  if (!ref) return null;
  if (ref.startsWith("data:")) return ref;
  try {
    const r = await fetch(photoUrl(ref));
    const blob = await r.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const SEVERITY_LABEL = {
  mineur: "Mineur",
  majeur: "Majeur",
  critique: "Critique",
};

const STATUS_LABEL = {
  actif: "Actif",
  inactif: "Inactif",
  rendu: "Rendu",
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR");
  } catch {
    return iso || "";
  }
};

const fmtDateOnly = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("fr-FR");
  } catch {
    return iso || "";
  }
};

export async function exportTruckReport({ truck, impacts, comments, history }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 36;
  let y = margin;

  // Header band
  doc.setFillColor(9, 9, 11);
  doc.rect(0, 0, pageW, 70, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("FLOTTE NY TRANS", margin, 32);
  doc.setFontSize(9);
  doc.setTextColor(212, 212, 216);
  doc.text("RAPPORT D'INSPECTION VÉHICULE", margin, 50);
  doc.text(
    `Émis le ${new Date().toLocaleString("fr-FR")}`,
    pageW - margin,
    50,
    { align: "right" }
  );
  y = 100;

  // Truck title
  doc.setTextColor(9, 9, 11);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(28);
  doc.text(truck.license_plate || "—", margin, y);
  y += 8;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(82, 82, 91);
  doc.text(
    `${truck.brand || ""} ${truck.model || ""} · ${truck.van_type.toUpperCase()}`,
    margin,
    y + 14
  );
  y += 28;

  // Status badges
  const drawBadge = (x, yy, label, fill) => {
    const w = doc.getTextWidth(label) + 14;
    doc.setFillColor(...fill);
    doc.rect(x, yy - 12, w, 16, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text(label.toUpperCase(), x + 7, yy);
    return x + w + 6;
  };
  const statusColors = {
    actif: [22, 163, 74],
    inactif: [113, 113, 122],
    rendu: [37, 99, 235],
  };
  let bx = margin;
  bx = drawBadge(bx, y, STATUS_LABEL[truck.status] || truck.status, statusColors[truck.status] || [113, 113, 122]);
  if (truck.red_tag) {
    bx = drawBadge(bx, y, "RED TAG · refusé Amazon", [220, 38, 38]);
  }
  y += 18;

  // Vehicle info table
  autoTable(doc, {
    startY: y,
    theme: "grid",
    headStyles: { fillColor: [9, 9, 11], textColor: [255, 255, 255], fontSize: 8 },
    bodyStyles: { fontSize: 10 },
    styles: { cellPadding: 6, lineColor: [228, 228, 231], lineWidth: 0.5 },
    head: [["Conducteur", "Année", "Kilométrage", "Mise à jour"]],
    body: [
      [
        truck.driver || "—",
        truck.year || "—",
        truck.mileage ? `${truck.mileage.toLocaleString()} km` : "—",
        fmtDate(truck.updated_at),
      ],
    ],
    margin: { left: margin, right: margin },
  });
  y = doc.lastAutoTable.finalY + 20;

  // Impacts summary
  const totalCost = impacts.reduce((s, i) => s + (i.cost || 0), 0);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(9, 9, 11);
  doc.text(`SINISTRES & IMPACTS (${impacts.length})`, margin, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(82, 82, 91);
  doc.text(
    `Coût total estimé : ${totalCost.toLocaleString()} €`,
    pageW - margin,
    y,
    { align: "right" }
  );
  y += 8;

  if (impacts.length === 0) {
    doc.setFontSize(10);
    doc.setTextColor(113, 113, 122);
    doc.text("Aucun impact enregistré.", margin, y + 12);
    y += 24;
  } else {
    autoTable(doc, {
      startY: y + 4,
      theme: "striped",
      headStyles: { fillColor: [9, 9, 11], textColor: [255, 255, 255], fontSize: 8 },
      bodyStyles: { fontSize: 9, valign: "top" },
      styles: { cellPadding: 5 },
      head: [["Date", "Gravité", "Chauffeur", "Description", "Position", "Coût (€)"]],
      body: impacts.map((i) => [
        fmtDateOnly(i.date),
        SEVERITY_LABEL[i.severity] || i.severity,
        i.driver || "—",
        i.description || "—",
        `x${i.position.x.toFixed(1)} y${i.position.y.toFixed(1)} z${i.position.z.toFixed(1)}`,
        i.cost ? i.cost.toLocaleString() : "0",
      ]),
      margin: { left: margin, right: margin },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 1) {
          const sev = impacts[data.row.index].severity;
          const colors = {
            mineur: [245, 158, 11],
            majeur: [234, 88, 12],
            critique: [220, 38, 38],
          };
          data.cell.styles.fillColor = colors[sev] || [113, 113, 122];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = "bold";
        }
      },
    });
    y = doc.lastAutoTable.finalY + 16;
  }

  // Comments
  if (y > pageH - 180) {
    doc.addPage();
    y = margin;
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(9, 9, 11);
  doc.text(`COMMENTAIRES (${comments.length})`, margin, y);
  y += 10;
  if (comments.length === 0) {
    doc.setFontSize(10);
    doc.setTextColor(113, 113, 122);
    doc.text("Aucun commentaire.", margin, y + 12);
    y += 24;
  } else {
    autoTable(doc, {
      startY: y + 4,
      theme: "grid",
      headStyles: { fillColor: [9, 9, 11], textColor: [255, 255, 255], fontSize: 8 },
      bodyStyles: { fontSize: 9, valign: "top" },
      styles: { cellPadding: 5 },
      head: [["Date", "Auteur", "Commentaire"]],
      body: comments.map((c) => [fmtDate(c.date), c.author, c.content]),
      margin: { left: margin, right: margin },
    });
    y = doc.lastAutoTable.finalY + 16;
  }

  // Status history
  if (y > pageH - 150) {
    doc.addPage();
    y = margin;
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(9, 9, 11);
  doc.text(`HISTORIQUE STATUT (${history.length})`, margin, y);
  y += 10;
  if (history.length === 0) {
    doc.setFontSize(10);
    doc.setTextColor(113, 113, 122);
    doc.text("Aucun changement de statut enregistré.", margin, y + 12);
    y += 24;
  } else {
    autoTable(doc, {
      startY: y + 4,
      theme: "grid",
      headStyles: { fillColor: [9, 9, 11], textColor: [255, 255, 255], fontSize: 8 },
      bodyStyles: { fontSize: 9 },
      styles: { cellPadding: 5 },
      head: [["Date", "Champ", "Avant", "Après", "Auteur"]],
      body: history.map((h) => [
        fmtDate(h.date),
        h.field === "red_tag" ? "RED TAG" : "Statut",
        h.from_value || "—",
        h.to_value || "—",
        h.author,
      ]),
      margin: { left: margin, right: margin },
    });
    y = doc.lastAutoTable.finalY + 16;
  }

  // Photos
  if (truck.photos && truck.photos.length > 0) {
    if (y > pageH - 200) {
      doc.addPage();
      y = margin;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(9, 9, 11);
    doc.text(`PHOTOS (${truck.photos.length})`, margin, y);
    y += 14;
    const photoW = (pageW - margin * 2 - 12) / 3;
    const photoH = photoW * 0.7;
    let col = 0;
    // Resolve all photo refs (storage paths -> data URLs) in parallel
    const resolved = await Promise.all(truck.photos.map(fetchAsDataURL));
    for (const dataUrl of resolved) {
      if (!dataUrl) {
        col++;
        continue;
      }
      if (col === 3) {
        col = 0;
        y += photoH + 8;
      }
      if (y + photoH > pageH - margin) {
        doc.addPage();
        y = margin;
        col = 0;
      }
      try {
        const fmt = dataUrl.startsWith("data:image/png") ? "PNG" : "JPEG";
        doc.addImage(
          dataUrl,
          fmt,
          margin + col * (photoW + 6),
          y,
          photoW,
          photoH,
          undefined,
          "FAST"
        );
      } catch (e) {
        // Photo may be unreadable, CORS-blocked or non-image — skip silently
        console.warn("Skipped photo in PDF:", e?.message || e);
      }
      col++;
    }
    y += photoH + 8;
  }

  // Footer page numbers
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(113, 113, 122);
    doc.text(
      `Page ${i} / ${pageCount}`,
      pageW / 2,
      pageH - 20,
      { align: "center" }
    );
    doc.text("FLOTTE NY TRANS · Rapport d'inspection", margin, pageH - 20);
  }

  doc.save(`rapport-${truck.license_plate || "camion"}-${Date.now()}.pdf`);
}